// Role constants shared across the shell (2026-09-11). Keep every
// cross-file role list HERE so the copies can't drift again (the phone
// dashboard list had three independent copies before this file existed).
//
// Roles: admin, site_manager, user, franchise, store_manager, crm,
// customer_service, finance, external (finance-like, but scoped to the
// user's own locations — Rush / Surf partners). The legacy 'location' role
// was removed 2026-09-11 (Eric) — no account ever carried it.

// Who gets the tile dashboard on a phone. Everyone else goes to Orders.
export const MOBILE_DASHBOARD_ROLES = ['admin', 'franchise', 'site_manager', 'user'];

// Who may print labels, pick lists and "Print selected" from the Orders list.
export const PRINT_ROLES = ['admin', 'site_manager', 'user', 'franchise'];

// Sidebar / phone-dashboard badge text.
export const ROLE_LABELS = {
    admin:            'Administrator',
    site_manager:     'Site Manager',
    user:             'User',
    franchise:        'Franchise',
    store_manager:    'Store Manager',
    crm:              'CRM',
    customer_service: 'Customer Service',
    finance:          'Finance',
    external:         'External',
};
export function roleLabel(role) {
    return ROLE_LABELS[role] ?? (role || '');
}

// ── Multiple roles per account (Eric, 2026-10-05) ────────────────────────────
// A person may hold several roles; what they may do is the UNION of them —
// every role adds, none subtracts. `user.roles` is the ordered list (primary
// first); accounts from before the change carry only `user.role`.
export function userRoles(user) {
    const list = Array.isArray(user?.roles) && user.roles.length ? user.roles : (user?.role ? [user.role] : []);
    return list.filter(Boolean);
}
// Allow-list gate: true when ANY of the person's roles is on the list.
export function hasRole(user, allowed) {
    const roles = userRoles(user);
    return roles.some(r => allowed.includes(r));
}
// Deny-list gate ("hidden for store managers"): true only when EVERY role is on the list.
export function allRolesIn(user, denied) {
    const roles = userRoles(user);
    return roles.length > 0 && roles.every(r => denied.includes(r));
}
// Extra permissions (Eric, 2026-10-08): per-account grants for individual
// admin-only items (keys from the server's USER_PERMISSIONS registry, served by
// /api/permissions and carried on /api/auth/verify as `permissions`). Admins
// hold every permission implicitly.
// Permissions that also come with a role (mirrors default_roles in app.py USER_PERMISSIONS).
const PERMISSION_DEFAULT_ROLES = { 'reports.backend': ['finance'] };
export function hasPermission(user, key) {
    if (hasRole(user, ['admin'])) return true;
    if (PERMISSION_DEFAULT_ROLES[key] && hasRole(user, PERMISSION_DEFAULT_ROLES[key])) return true;
    return (user?.permissions || []).includes(key);
}
// Roles whose view is scoped by location: an account needs at least one location
// when ANY of its roles is one of these (mirrors app.py _ROLES_REQUIRE_LOCATION).
export const ROLES_REQUIRE_LOCATION = ['user', 'crm', 'external'];
export function rolesRequireLocation(roles) {
    return (roles || []).some(r => ROLES_REQUIRE_LOCATION.includes(r));
}
// Badge text: every role the person holds, e.g. "Site Manager · CRM".
export function roleBadge(user) {
    return userRoles(user).map(roleLabel).join(' · ');
}
